import React, { FC, useEffect, useState } from 'react';
import { Field, Select } from '@kontentino/ui';
import { SuggestionApi } from 'api/suggestions';
import { LOCATION_QUERY_MIN_LENGTH } from 'app/modules/textEditor/constants/textEditorFeature';
import { COPY_KEY_BY_REASON } from 'app/modules/textEditor/components/MentionSearchFailureNotice';
import PageTypeUtils from 'app/utils/pageType';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import useDebounce from 'utils/hooks/useDebounce';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { useTranslation } from 'react-i18next';

type Props = {
  // The location search is page-scoped now: it spends this page's own token,
  // exactly as the `@` typeahead does.
  pageId: number;
  onChange: (value: { id: string; name: string } | null) => void;
  value?: {
    id: string;
    name: string;
  } | null;
};

const PostComposerContentInstagramLocation: FC<Props> = ({
  pageId,
  onChange,
  value,
}) => {
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebounce(inputValue, 500);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();

  const {
    data: searchData,
    isLoading,
    isError,
  } = useQuery(
    queryKey.locationsSearch(debouncedSearch, pageId),
    () =>
      SuggestionApi.locations({
        pageId: pageId,
        query: debouncedSearch,
      }),
    {
      enabled: debouncedSearch.length >= LOCATION_QUERY_MIN_LENGTH,
    },
  );

  useEffect(() => {
    if (inputValue === '' && value) {
      setInputValue(value.name);
    }
  }, [inputValue, value]);

  return (
    <Field.Group>
      <Field.Label required>{t('location')}</Field.Label>
      <div
        data-name="posts-composer_post-content_location"
        className="tw-h-full tw-min-h-[2.5rem]"
      >
        <Select
          menuIsOpen={menuOpen}
          onMenuOpen={() => {
            setMenuOpen(true);

            if (inputValue === '' && value) {
              setInputValue(value.name);
            }
          }}
          onMenuClose={() => {
            setMenuOpen(false);
          }}
          isClearable={true}
          isLoading={isLoading}
          name="pageIdForLocation"
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          value={
            value
              ? {
                  value: value.id,
                  label: value.name ? value.name : t('unknownLocation'),
                }
              : null
          }
          inputValue={menuOpen ? inputValue : ''}
          options={
            searchData?.items.map((option) => ({
              value: option.id.toString(),
              label: option.name,
            })) ?? []
          }
          placeholder=""
          onInputChange={setInputValue}
          onChange={(newValue) => {
            onChange(
              newValue
                ? {
                    id: newValue.value,
                    name: newValue.label,
                  }
                : null,
            );

            // Report only a real selection, never a clear: a `null` here
            // means the user removed the location, and counting a removal
            // as a use would inflate the very figure this feature exists
            // to measure.
            if (newValue) {
              SuggestionApi.reportSelection({
                pageId,
                entityKind: 'location',
                entityId: newValue.value,
                entityName: newValue.label,
                source: 'location_picker',
              });
            }
          }}
          noOptionsMessage={() => {
            if (!inputValue) {
              return t('noOptionsMessageInitial');
            }

            // Below the minimum, no request is made at all — so saying
            // "no place matches" would report on a search that never ran.
            if (debouncedSearch.length < LOCATION_QUERY_MIN_LENGTH) {
              return t('noOptionsMessageInitial');
            }

            // The request itself failed. `searchData` is undefined here, and
            // falling through to the empty copy would claim nothing matched —
            // the exact lie this phase exists to remove, reintroduced one
            // layer up.
            if (isError) {
              return t('mentions.search.transportError');
            }

            // A typed failure is the case the old endpoint could not express
            // at all: it answered a dead connection and a genuine no-match
            // identically, so the picker said "no results" either way and the
            // user retried a search that could never work.
            if (searchData?.failure) {
              return t(COPY_KEY_BY_REASON[searchData.failure.reason], {
                network: PageTypeUtils.getLabel(TYPE.INSTAGRAM),
                pageName: searchData.failure.pageName,
              });
            }

            // Still in flight, or not yet resolved. Nothing has been learned
            // about this query, so nothing is claimed about it.
            if (!searchData) {
              return t('noOptionsMessageInitial');
            }

            // The advice that used to live only in the help centre. Most misses
            // are a real place Facebook has no geo-coded Page for, and both of
            // these actually work — so saying them here is worth more than a
            // link nobody follows mid-compose.
            return t('mentions.locations.empty');
          }}
          styles={{
            menu: (base) => ({
              ...base,
              zIndex: 10,
            }),
          }}
          filterOption={null}
        />
      </div>
    </Field.Group>
  );
};

export default PostComposerContentInstagramLocation;
